import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { X, Sparkles } from "lucide-react";

export default function PackCard({ pack, foodProducts, drinkProducts }) {
  const [open, setOpen] = useState(false);
  const { addPack } = useCart();

  const saving = pack.original_eur && pack.original_eur > pack.price_eur
    ? pack.original_eur - pack.price_eur
    : 0;

  return (
    <>
      <article className="group relative border-2 border-[#14230A] bg-[#FDF0DF] card-lift flex flex-col overflow-hidden">
        {pack.image_url && (
          <>
            <img
              src={pack.image_url}
              alt=""
              aria-hidden="true"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
            />
            <div className="absolute inset-0 bg-[#FDF0DF]/60 group-hover:bg-[#14230A]/80 transition-colors duration-500 pointer-events-none" />
          </>
        )}

        <div className="relative flex items-stretch border-b-2 border-[#14230A] group-hover:border-[#FDF0DF] transition-colors z-10">
          <div className="flex-1 px-4 py-2 font-cond uppercase text-[11px] tracking-[0.25em] flex items-center gap-2 group-hover:text-[#FDF0DF] transition-colors">
            <Sparkles className="w-3 h-3" /> Pack soirée
          </div>
          {saving > 0 && (
            <div className="px-3 py-2 bg-[#FF4D00] text-[#FDF0DF] font-cond uppercase text-[11px] tracking-[0.2em] border-l-2 border-[#14230A] group-hover:border-[#FDF0DF]">
              −{saving.toFixed(0)} €
            </div>
          )}
        </div>

        <div className="relative p-5 flex flex-col flex-1 z-10 group-hover:text-[#FDF0DF] transition-colors duration-300">
          <h3 className="font-display text-3xl md:text-4xl leading-[0.9]">{pack.name}</h3>
          <div className="mt-1 font-cond text-[11px] tracking-[0.3em] uppercase text-[#FF4D00]">
            {pack.subtitle}
          </div>
          <p className="mt-3 text-sm opacity-75 leading-snug min-h-[40px]">{pack.description}</p>

          <div className="mt-4 flex items-baseline gap-3">
            <span className="font-display text-5xl">{pack.price_eur.toFixed(0)}</span>
            <span className="font-cond text-sm tracking-widest opacity-70">€ / pack</span>
            {saving > 0 && (
              <span className="font-cond text-sm line-through opacity-50">
                {pack.original_eur.toFixed(0)} €
              </span>
            )}
          </div>

          <div className="mt-3 font-cond uppercase text-[10px] tracking-[0.25em] opacity-60">
            {pack.pack_n_food} food{pack.pack_n_food > 1 ? "s" : ""} · {pack.pack_n_drinks} boisson{pack.pack_n_drinks > 1 ? "s" : ""}
          </div>

          <button
            onClick={() => setOpen(true)}
            data-testid={`pack-configure-${pack.id}`}
            className="mt-5 w-full h-11 bg-[#14230A] text-[#FDF0DF] font-cond uppercase tracking-[0.18em] text-sm font-bold group-hover:bg-[#FF4D00] hover:bg-[#FF4D00] transition-colors"
          >
            Configurer ce pack
          </button>
        </div>
      </article>

      {open && (
        <PackConfigDialog
          pack={pack}
          foodProducts={foodProducts}
          drinkProducts={drinkProducts}
          onClose={() => setOpen(false)}
          onConfirm={(components) => {
            addPack(pack, components);
            setOpen(false);
            toast.success(`${pack.name} ajouté au panier`);
          }}
        />
      )}
    </>
  );
}

function PackConfigDialog({ pack, foodProducts, drinkProducts, onClose, onConfirm }) {
  const [foodChoices, setFoodChoices] = useState(() =>
    Array.from({ length: pack.pack_n_food }, () => foodProducts[0]?.id || "")
  );
  const [drinkChoices, setDrinkChoices] = useState(() =>
    Array.from({ length: pack.pack_n_drinks }, () => drinkProducts[0]?.id || "")
  );

  const availableFood = useMemo(() => foodProducts.filter((p) => p.stock_remaining > 0), [foodProducts]);
  const availableDrinks = useMemo(
    () => drinkProducts.filter((p) => p.stock_remaining > 0),
    [drinkProducts]
  );

  // Aggregate count per product to check stock collectively
  const aggregate = (choices) => {
    const counts = {};
    choices.forEach((id) => {
      counts[id] = (counts[id] || 0) + 1;
    });
    return counts;
  };

  const foodCounts = aggregate(foodChoices);
  const drinkCounts = aggregate(drinkChoices);

  const insufficientStock = useMemo(() => {
    const all = [...Object.entries(foodCounts), ...Object.entries(drinkCounts)];
    return all.find(([id, qty]) => {
      const p = [...foodProducts, ...drinkProducts].find((x) => x.id === id);
      return !p || p.stock_remaining < qty;
    });
  }, [foodCounts, drinkCounts, foodProducts, drinkProducts]);

  const totalRetail = useMemo(() => {
    const sum = (arr, src) =>
      arr.reduce((s, id) => {
        const p = src.find((x) => x.id === id);
        return s + (p?.price_eur || 0);
      }, 0);
    return sum(foodChoices, foodProducts) + sum(drinkChoices, drinkProducts);
  }, [foodChoices, drinkChoices, foodProducts, drinkProducts]);

  const saving = totalRetail - pack.price_eur;

  const handleConfirm = () => {
    if (insufficientStock) {
      toast.error("Stock insuffisant sur l'un des choix");
      return;
    }
    const findProd = (id) => [...foodProducts, ...drinkProducts].find((p) => p.id === id);
    const buildComponents = (counts) =>
      Object.entries(counts).map(([id, qty]) => {
        const p = findProd(id);
        return {
          product_id: id,
          name: p?.name || id,
          quantity: qty,
          category: p?.category,
          is_alcohol: !!p?.is_alcohol,
        };
      });
    const components = [...buildComponents(foodCounts), ...buildComponents(drinkCounts)];
    onConfirm(components);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="!max-w-2xl bg-[#FDF0DF] border-2 border-[#14230A] text-[#14230A] p-0 rounded-none"
        data-testid={`pack-dialog-${pack.id}`}
      >
        <DialogHeader className="px-6 pt-6">
          <div className="font-cond uppercase tracking-[0.3em] text-[11px] text-[#FF4D00] flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Configure ton pack
          </div>
          <DialogTitle className="font-display text-4xl md:text-5xl leading-none mt-1">
            {pack.name}
          </DialogTitle>
          <DialogDescription className="text-[#14230A]/70">
            {pack.subtitle} · prix fixe {pack.price_eur.toFixed(0)} €
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 pb-2 max-h-[60vh] overflow-y-auto space-y-6">
          {/* Food slots */}
          <div>
            <div className="font-cond uppercase tracking-[0.2em] text-[11px] mb-2 flex items-center gap-2">
              <span className="w-5 h-5 bg-[#14230A] text-[#FDF0DF] inline-flex items-center justify-center text-[11px] font-bold">
                {pack.pack_n_food}
              </span>
              Food
            </div>
            <div className="space-y-2">
              {foodChoices.map((choice, i) => (
                <select
                  key={i}
                  data-testid={`pack-food-select-${pack.id}-${i}`}
                  value={choice}
                  onChange={(e) => {
                    const next = [...foodChoices];
                    next[i] = e.target.value;
                    setFoodChoices(next);
                  }}
                  className="w-full h-12 px-3 border-2 border-[#14230A] bg-[#FDF0DF] font-cond text-base focus:border-[#FF4D00] outline-none"
                >
                  {availableFood.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} — {p.price_eur.toFixed(2)} €
                    </option>
                  ))}
                </select>
              ))}
            </div>
          </div>

          {/* Drink slots */}
          <div>
            <div className="font-cond uppercase tracking-[0.2em] text-[11px] mb-2 flex items-center gap-2">
              <span className="w-5 h-5 bg-[#14230A] text-[#FDF0DF] inline-flex items-center justify-center text-[11px] font-bold">
                {pack.pack_n_drinks}
              </span>
              Boisson{pack.pack_n_drinks > 1 ? "s" : ""}
            </div>
            <div className="space-y-2">
              {drinkChoices.map((choice, i) => (
                <select
                  key={i}
                  data-testid={`pack-drink-select-${pack.id}-${i}`}
                  value={choice}
                  onChange={(e) => {
                    const next = [...drinkChoices];
                    next[i] = e.target.value;
                    setDrinkChoices(next);
                  }}
                  className="w-full h-12 px-3 border-2 border-[#14230A] bg-[#FDF0DF] font-cond text-base focus:border-[#FF4D00] outline-none"
                >
                  {availableDrinks.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.is_alcohol ? "🍺 " : "🥤 "}
                      {p.name} — {p.price_eur.toFixed(2)} €
                    </option>
                  ))}
                </select>
              ))}
            </div>
          </div>

          {saving > 0 && (
            <div className="bg-[#FF4D00] text-[#FDF0DF] p-3 text-sm">
              <b>Économie : {saving.toFixed(2)} €</b> par rapport au prix à la carte ({totalRetail.toFixed(2)} €).
            </div>
          )}
          {insufficientStock && (
            <div className="border-2 border-[#C53A1B] text-[#C53A1B] p-3 text-sm">
              ⚠ Stock insuffisant sur l’un des choix. Modifie ta sélection.
            </div>
          )}
        </div>

        <div className="px-6 py-5 border-t-2 border-[#14230A] flex flex-col sm:flex-row gap-2">
          <button
            onClick={onClose}
            className="h-12 px-5 border-2 border-[#14230A] font-cond uppercase tracking-[0.2em] text-xs hover:bg-[#14230A] hover:text-[#FDF0DF]"
          >
            Annuler
          </button>
          <button
            data-testid={`pack-confirm-${pack.id}`}
            onClick={handleConfirm}
            disabled={!!insufficientStock}
            className="flex-1 h-12 bg-[#14230A] text-[#FDF0DF] font-cond uppercase tracking-[0.2em] text-sm font-bold hover:bg-[#FF4D00] disabled:opacity-50"
          >
            Ajouter au panier · {pack.price_eur.toFixed(2)} €
          </button>
        </div>

        <button
          onClick={onClose}
          aria-label="Fermer"
          className="absolute top-3 right-3 w-9 h-9 border-2 border-[#14230A] flex items-center justify-center hover:bg-[#14230A] hover:text-[#FDF0DF]"
        >
          <X className="w-4 h-4" />
        </button>
      </DialogContent>
    </Dialog>
  );
}
